import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/movies" },
  { label: "电视剧", to: "/tvshows" },
  { label: "影视资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setSuggestOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  const suggestions = keyword.trim()
    ? ["星际苍穹", "深海回声", "幻影城市", "迷雾档案", "量子裂缝", "长风渡口", "影视资讯"].filter((item) =>
        item.includes(keyword.trim())
      )
    : [];

  const submitSearch = (value?: string) => {
    const q = (value ?? keyword).trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setSuggestOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-film-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-lg font-bold tracking-wide text-foreground-950 md:text-xl">四虎网站</span>
            <span className="mt-0.5 hidden text-[10px] tracking-[0.22em] text-foreground-400 sm:block">
              SI HU FILM
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-50 font-medium text-primary-600"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <div className="hidden items-center md:flex">
            {searchOpen ? (
              <div className="relative">
                <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 pl-3 pr-1 py-1">
                  <i className="ri-search-line text-foreground-400" />
                  <input
                    ref={inputRef}
                    value={keyword}
                    onChange={(e) => {
                      setKeyword(e.target.value);
                      setSuggestOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitSearch();
                    }}
                    placeholder="搜索影片、剧集或资讯"
                    className="w-40 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400 lg:w-56"
                  />
                  <button
                    type="button"
                    onClick={() => submitSearch()}
                    className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-600"
                  >
                    搜索
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchOpen(false);
                      setSuggestOpen(false);
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
                {suggestOpen && suggestions.length > 0 && (
                  <ul className="absolute right-0 top-full mt-2 w-full overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-sm">
                    {suggestions.map((s) => (
                      <li key={s}>
                        <button
                          type="button"
                          onClick={() => {
                            setKeyword(s);
                            submitSearch(s);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground-700 hover:bg-background-100"
                        >
                          <i className="ri-search-line text-foreground-400" />
                          {s}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 rounded-full border border-background-300 px-3 py-1.5 text-sm text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-search-line" />
                <span className="hidden lg:inline">搜索</span>
              </button>
            )}
          </div>

          <Link
            to="/movies"
            className="hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 lg:inline-block"
          >
            立即观影
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
            aria-label="打开菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto bg-background-50 lg:hidden">
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitSearch();
                }}
                placeholder="搜索影片、剧集或资讯"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
              <button type="button" onClick={() => submitSearch()} className="text-primary-600">
                <i className="ri-arrow-right-line text-lg" />
              </button>
            </div>
            <nav className="mt-4 flex flex-col">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-lg px-3 py-3.5 text-base transition-colors ${
                      isActive ? "bg-primary-50 font-medium text-primary-600" : "text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
                </NavLink>
              ))}
            </nav>
            <Link
              to="/movies"
              className="mt-4 block whitespace-nowrap rounded-md bg-accent-500 px-4 py-3 text-center text-sm font-medium text-background-50"
            >
              立即观影
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}